import { supabase } from "../supabase";

/**
 * Hours API helpers using Supabase
 * Manages user_hours and pair_hours tables
 */

export interface UserHours {
  id: number;
  user_id: string;
  package_type: string;
  hours_remaining: number;
  hours_used: number;
  last_transaction_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface PairHours {
  id: number;
  pair_id: number;
  package_type: string;
  hours_remaining: number;
  hours_used: number;
  last_transaction_id: number | null;
  created_at: string;
  updated_at: string;
}

export const hoursApi = {
  /**
   * Fetch user hours for a specific user
   */
  async getUserHours(
    userId: string,
    packageType?: string,
  ): Promise<{
    success: boolean;
    data?: UserHours | UserHours[];
    error?: string;
  }> {
    try {
      let query = supabase.from("user_hours").select("*").eq("user_id", userId);

      if (packageType) {
        query = query.eq("package_type", packageType);
        const { data, error } = await query.maybeSingle();

        // maybeSingle() vracia null keď záznam neexistuje (nie error)
        if (error) {
          // Len skutočné chyby (nie "not found")
          if (error.code !== "PGRST116") {
            throw error;
          }
          // PGRST116 = no rows returned - to je OK, záznam jednoducho neexistuje
          return { success: true, data: undefined };
        }

        return { success: true, data: (data as UserHours) || undefined };
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data: data as UserHours[] };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch user hours",
      };
    }
  },

  /**
   * Fetch pair hours for a specific pair
   */
  async getPairHours(
    pairId: number,
    packageType?: string,
  ): Promise<{
    success: boolean;
    data?: PairHours | PairHours[];
    error?: string;
  }> {
    try {
      let query = supabase.from("pair_hours").select("*").eq("pair_id", pairId);

      if (packageType) {
        query = query.eq("package_type", packageType);
        const { data, error } = await query.single();
        if (error) throw error;
        return { success: true, data: data as PairHours };
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data: data as PairHours[] };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch pair hours",
      };
    }
  },

  /**
   * Update user hours (usually done via transaction, but can be manual)
   */
  async updateUserHours(
    userId: string,
    packageType: string,
    updates: {
      hours_remaining?: number;
      hours_used?: number;
      last_transaction_id?: number;
    },
  ): Promise<{ success: boolean; data?: UserHours; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("user_hours")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", userId)
        .eq("package_type", packageType)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as UserHours };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update user hours",
      };
    }
  },

  /**
   * Update pair hours
   */
  async updatePairHours(
    pairId: number,
    packageType: string,
    updates: {
      hours_remaining?: number;
      hours_used?: number;
      last_transaction_id?: number;
    },
  ): Promise<{ success: boolean; data?: PairHours; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("pair_hours")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("pair_id", pairId)
        .eq("package_type", packageType)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as PairHours };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update pair hours",
      };
    }
  },
};
